import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import { contentPrinciples, testimonials, siteInfo, heroStats } from "@/mocks/site";

const milestones = [
  {
    year: "2021",
    title: "从一条线索开始",
    desc: "最初只是一间小工作室在做的线索整理，第一批投稿来自十几位不愿露面的行业从业者。",
  },
  {
    year: "2023",
    title: "建立双源核实机制",
    desc: "开始要求每一条上墙内容至少经过两条独立信息源比对，发布节奏因此整体变慢。",
  },
  {
    year: "2024",
    title: "投稿入口全面匿名化",
    desc: "取消实名与手机号强制填写，截图与聊天记录统一重制后再对外呈现。",
  },
  {
    year: "2026",
    title: "六个常驻栏目成型",
    desc: "娱乐、网红、商业、职场、校园与生活六个方向各自独立运营，线索流转更清晰。",
  },
];

export default function AboutPage() {
  const aboutJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 瓜友会网",
      url: "https://jfinger.cn/about",
      description:
        "瓜友会网的品牌介绍、站点定位、内容原则、发展历程与联系方式，了解匿名爆料社区是如何运转的。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://jfinger.cn",
      email: siteInfo.email,
      description: siteInfo.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://jfinger.cn" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://jfinger.cn/about" },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 瓜友会网 | 匿名爆料社区的内容原则与立场"
        description="了解瓜友会网是谁：我们如何做线索征集与匿名核实，坚持哪些内容原则，如何处理纠错与撤回，以及完整的投稿联系渠道。"
        keywords="瓜友会网简介,匿名爆料社区,内容原则,线索征集,投稿联系"
        path="/about"
        jsonLd={aboutJsonLd}
      />
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-9 md:px-6 md:py-12 lg:px-10">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <p className="mb-4 text-xs font-medium tracking-[0.28em] text-primary-600">ABOUT GUAYOUHUI</p>
                <h1 className="font-heading text-3xl font-bold leading-[1.35] tracking-tight text-foreground-950 md:text-[40px] md:leading-[1.3]">
                  一个让线索安心落地的地方
                </h1>
                <p className="mt-5 max-w-2xl text-[15px] leading-8 text-foreground-700">
                  {siteInfo.description}
                </p>
                <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
                  {heroStats.map((stat) => (
                    <div key={stat.label}>
                      <p className="font-heading text-2xl font-bold text-foreground-950">
                        {stat.value}
                        <span className="ml-1 text-xs font-normal text-foreground-500">{stat.unit}</span>
                      </p>
                      <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="h-[240px] w-full overflow-hidden rounded-lg border border-background-200 md:h-[300px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20an%20anonymous%20messenger%20dropping%20a%20sealed%20envelope%20into%20a%20slot%20with%20warm%20amber%20light%20and%20deep%20olive%20shadows%2C%20minimal%20artistic%20composition%2C%20magazine%20style%2C%20no%20text&width=1200&height=800&seq=gy-hub-about-01&orientation=landscape"
                    alt="瓜友会网匿名投稿与线索征集示意配图"
                    title="瓜友会网 - 匿名爆料与线索征集"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-16 md:px-6 md:py-20 lg:px-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[30px]">
                我们是谁，在做什么
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-8 text-foreground-700">
                <p>
                  瓜友会网成立于 2021 年，起因很简单：很多手里有料的人，找不到一个既能说清楚事情、又不用暴露自己的地方。
                  于是我们把这件事做了下来，先做整理，再做核实，最后才谈发布。
                </p>
                <p>
                  我们不是传统意义上的媒体机构，也不打算成为情绪宣泄的出口。我们的定位更接近一个"线索中转站"：
                  接住投稿者递过来的信息，用可核对的方式把它变成能够被公众理解的稿件。
                </p>
                <p>
                  因此在选择内容时，我们更在意"能不能验证"，而不是"够不够炸"。这也意味着有些非常热闹的消息，
                  我们最终选择了不发。慢一点，是我们认为值得付出的代价。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[30px]">
                内容原则
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {contentPrinciples.map((item, index) => (
                  <Reveal key={item.title} delay={index * 80}>
                    <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                      <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                        <i className={item.icon} aria-hidden="true" />
                      </span>
                      <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-16 md:px-6 md:py-20 lg:px-10">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[30px]">
              一路走来的几个节点
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-8 text-foreground-600">
              发展节奏一直不快，但每一步都围绕着同一件事：让投稿者更安全一点。
            </p>
            <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {milestones.map((item, index) => (
                <Reveal key={item.year} delay={index * 90}>
                  <li className="relative flex h-full flex-col rounded-lg border border-background-300 bg-background-50 p-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-background-50">
                      {item.year}
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-16 md:px-6 md:py-20 lg:px-10">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[30px]">
            瓜友们的反馈
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {testimonials.map((item, index) => (
              <Reveal key={item.name} delay={index * 70}>
                <figure className="flex h-full gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-800">
                    <i className={item.avatar} aria-hidden="true" />
                  </span>
                  <div>
                    <blockquote className="text-sm leading-8 text-foreground-700">{item.content}</blockquote>
                    <figcaption className="mt-4 text-xs text-foreground-500">
                      <span className="font-medium text-foreground-900">{item.name}</span>
                      <span className="mx-2 text-background-400">|</span>
                      {item.role}
                    </figcaption>
                  </div>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full px-4 pb-16 md:px-6 md:pb-20 lg:px-10">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100 p-8 lg:grid-cols-12 lg:p-10">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
                联系我们
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-8 text-foreground-600">
                投稿、纠错、合作与侵权申诉都可以通过下面两个渠道联系。为避免来源泄露，
                我们不接受电话与即时通讯工具的直接联系。
              </p>
              <dl className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <dt className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-mail-send-line text-sm text-primary-600" aria-hidden="true" />
                    投稿与纠错邮箱
                  </dt>
                  <dd className="mt-2 text-sm text-foreground-900">{siteInfo.email}</dd>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <dt className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-time-line text-sm text-primary-600" aria-hidden="true" />
                    处理时间
                  </dt>
                  <dd className="mt-2 text-sm text-foreground-900">{siteInfo.workTime}</dd>
                </div>
              </dl>
            </div>
            <div className="lg:col-span-5">
              <div className="flex h-full flex-col justify-center rounded-lg bg-secondary-950 p-7 text-background-100">
                <h3 className="font-heading text-xl font-bold text-background-50">手里还有没投的线索？</h3>
                <p className="mt-3 text-sm leading-8 text-background-300">
                  匿名投稿通道全天开放，可以先只提交一段描述，细节我们在核实阶段再确认。
                </p>
                <Link
                  to="/submit"
                  className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true" />
                  前往投稿入口
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}